import React, { ReactElement, Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { WindowLocation } from "@reach/router";

import { MobilistTipData, ContentfulCombinatorSection, ContentfulForm, ContentfulPhone } from "../../../../ContentfulTypes";
import MobilistTip from "../MobilistTip/MobilistTip";
import FilterError from "../FilterError/FilterError";
import Loader from "../../../UI/Loader/Loader";
import formatPhoneOffers from '../../../../utils/formatPhoneOffers';
import { setPhonesCount } from '../../../../redux/reducers/phoneReducer';
import { filtersSelector } from '../../../../redux/selectors/filtersSelectors';
import PhoneCard from '../../../PhoneCard/PhoneCard';
import createPhoneCard from "../utils/createPhoneCard";
import categorizePhone from "../utils/categorizePhone";
import applyPCOptimumFilter from "../utils/applyPcOptimumFilter";
import phoneFiltersVerification from "../utils/phoneFiltersVerification";

import { LoaderWrapper } from "./styles";

const ProductList = React.lazy(() => import("../ProductList/ProductList"));

interface Props {
  isLoading: boolean;
  standardNoResults: ContentfulCombinatorSection;
  mobilistTipsData: Array<MobilistTipData>,
  isMsaActive: boolean;
  prepaidPhonesHeading: string;
  tabletHeading: string;
  phoneHeading: string;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  province?: string;
  location: WindowLocation<unknown>;
  phoneLabelData: ContentfulForm;
  products: Array<ContentfulPhone>;
}

// How many phones to display before inserting a mobilist tip
// If tip would be the last item visible in the grid, it will not be displayed
const mobilistTileFrequency = 8;

const ProductsView = ({
  isLoading,
  standardNoResults,
  mobilistTipsData,
  isMsaActive,
  prepaidPhonesHeading,
  tabletHeading,
  phoneHeading,
  setIsLoading,
  phoneLabelData,
  province,
  products,
  location,
}: Props) => {
  const dispatch = useDispatch();
  const storedFilters = useSelector(filtersSelector);
  // Phones that pass all category tests will be pushed to this array and rendered
  const [filteredPostpaidPhones, setFilteredPostpaidPhones] = useState<
    ReactElement[]
  >([]);
  const [filteredTablets, setFilteredTablets] = useState<ReactElement[]>([]);
  const [filteredPrepaidPhones, setFilteredPrepaidPhones] = useState<
    ReactElement[]
  >([]);
  const PhoneComponent = PhoneCard;

  useEffect(() => {
    const filterPhones = () => {
      const categories = {
        postpaidPhones: [],
        tablets: [],
        prepaidPhones: [],
      };
  
      products.forEach((phone, index) => {
        const formattedPhone = formatPhoneOffers(phone, province);
  
        if (!formattedPhone.isPhoneActive || !formattedPhone.availableOffers.length) return;
  
        if (!phoneFiltersVerification(formattedPhone, storedFilters)) return;
  
        const PhoneElement = createPhoneCard({
          phone: formattedPhone,
          index,
          location,
          phoneLabelData,
        });
        categorizePhone(formattedPhone, PhoneElement, categories);
      });
  
      return categories;
    };
  
    const filteredPhones = filterPhones();
    const filteredPhonesWithPcOptimumFilter = applyPCOptimumFilter(
      { ...filteredPhones, storedFilters },
    );
    setFilteredPostpaidPhones(filteredPhonesWithPcOptimumFilter.postpaidPhones);
    setFilteredTablets(filteredPhonesWithPcOptimumFilter.tablets);
    setFilteredPrepaidPhones(filteredPhonesWithPcOptimumFilter.prepaidPhones);
    setIsLoading(false);
  }, [
    PhoneComponent,
    location,
    phoneLabelData.formFields,
    products,
    province,
    storedFilters,
    phoneLabelData,
    setIsLoading,
  ]);

  // Update MSA filter with new total phones count
  const totalPhoneCount = filteredPostpaidPhones.length + filteredPrepaidPhones.length;
  useEffect(() => {
    dispatch(setPhonesCount(totalPhoneCount));
  }, [dispatch, totalPhoneCount]);

  if (isLoading) {
    return (
      <LoaderWrapper>
        <Loader className="small m-auto" />
      </LoaderWrapper>
    );
  }

  const isError = filteredPostpaidPhones.length === 0
    && filteredPrepaidPhones.length === 0
    && filteredTablets.length === 0;
  if (isError) {
    return (
      <FilterError standardNoResults={standardNoResults} />
    );
  }

  // Create array of mobilist tips
  const mobilistTipsArray = mobilistTipsData.map((tip) => (
    <MobilistTip
      heading={tip.tipHeading}
      body={tip.tipBody}
      mobilistImage={tip.mobilistImage}
      cta={tip.tipCta}
      key={tip.contentful_id}
    />
  ));

  return (
    <>
      {filteredPostpaidPhones.length > 0 && (
        <Suspense>
          <ProductList
            heading={phoneHeading}
            items={filteredPostpaidPhones}
            countStringSingularId="PhoneFound"
            countStringPluralId="PhonesFound"
            msaActive={isMsaActive}
            showMobilistTip
            mobilistTileFrequency={mobilistTileFrequency}
            mobilistTipsArray={mobilistTipsArray}
            dataTestId="postPaidPhones"
          />
        </Suspense>
      )}
      {/* Prepaid Phones Section */}
      {filteredPrepaidPhones.length > 0 && (
        <Suspense>
          <ProductList
            heading={prepaidPhonesHeading}
            items={filteredPrepaidPhones}
            countStringSingularId="PhoneFound"
            countStringPluralId="PhonesFound"
            msaActive={isMsaActive}
            dataTestId="prePaidPhones"
          />
        </Suspense>
      )}
      {/* Tablet Section */}
      {filteredTablets.length > 0 && (
        <Suspense>
          <ProductList
            heading={tabletHeading}
            items={filteredTablets}
            countStringSingularId="TabletFound"
            countStringPluralId="TabletsFound"
            msaActive={isMsaActive}
            dataTestId="tablets"
          />
        </Suspense>
      )}
    </>
  );
};

export default ProductsView;
